/**
 * The patient's file as one story, newest first.
 *
 * QA report §1.2 asked for a unified medical file every department can read.
 * GET /api/patients/:id/file already returns everything — but as nine lists,
 * and a nurse who wants to know "what happened to this patient today" should
 * not have to open nine tabs and merge them in her head. This merges them:
 * the visit and what the desk heard at it, the receipt and what it covered,
 * the requests it routed, their results, the diagnoses, the vitals, the
 * uploads, the beds.
 *
 * Pure: the file in, rows out. Every row carries its own words, so the view
 * only lays them out, and the unit test can read exactly what a desk will.
 * Money appears only when the server sent it — this never decides who may
 * see a price.
 */

import { ASSIGNMENT_META, BILLING_META, DEPARTMENT_META, STATUS_META, formatMoney } from './labels';

export const TIMELINE_KINDS = Object.freeze({
  registration: { label: 'فتح الملف', icon: 'folder-plus', tone: 'cyan', group: 'file' },
  visit: { label: 'زيارة', icon: 'calendar-days', tone: 'blue', group: 'clinical' },
  receipt: { label: 'إيصال', icon: 'receipt', tone: 'emerald', group: 'money' },
  request: { label: 'طلب خدمة', icon: 'send', tone: 'purple', group: 'services' },
  result: { label: 'نتيجة', icon: 'file-check-2', tone: 'teal', group: 'services' },
  note: { label: 'تشخيص / ملاحظة', icon: 'notebook-pen', tone: 'amber', group: 'clinical' },
  prescription: { label: 'روشتة', icon: 'scroll-text', tone: 'orange', group: 'clinical' },
  nursing: { label: 'تمريض', icon: 'activity', tone: 'rose', group: 'clinical' },
  bed: { label: 'إقامة', icon: 'bed-double', tone: 'blue', group: 'clinical' },
  attachment: { label: 'مرفق', icon: 'paperclip', tone: 'slate', group: 'file' },
});

/** The filter chips over the timeline. `null` group means everything. */
export const TIMELINE_FILTERS = Object.freeze([
  { id: 'all', label: 'الكل', group: null },
  { id: 'clinical', label: 'طبي', group: 'clinical' },
  { id: 'services', label: 'الخدمات والنتائج', group: 'services' },
  { id: 'money', label: 'الإيصالات', group: 'money' },
  { id: 'file', label: 'الملف والمرفقات', group: 'file' },
]);

const NOTE_LABELS = {
  initial_diagnosis: 'تشخيص مبدئي',
  final_diagnosis: 'تشخيص نهائي',
  complaint: 'شكوى',
  note: 'ملاحظة',
  ai_summary: 'ملخص الذكاء الاصطناعي',
};

const RECEIPT_STATUS = { issued: 'صادر', collected: 'محصّل', void: 'ملغي' };

const VITALS = [
  ['bp', 'الضغط', ''],
  ['pulse', 'النبض', '/د'],
  ['temperature', 'الحرارة', '°'],
  ['spo2', 'الأكسجين', '%'],
  ['respiratoryRate', 'التنفس', '/د'],
  ['glucose', 'السكر', ''],
];

const clip = (text, max = 180) => {
  const s = String(text ?? '').replace(/\s+/g, ' ').trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

/** A moment as epoch ms, or NaN. A bare YYYY-MM-DD is that day's local noon. */
export function momentOf(value, time = null) {
  if (value === null || value === undefined || value === '') return NaN;
  if (value instanceof Date) return value.getTime();
  const text = String(value);
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (day) {
    let hours = 12;
    let minutes = 0;
    const clock = /(\d{1,2}):(\d{2})\s*([AaPp][Mm]|ص|م)?/.exec(String(time || ''));
    if (clock) {
      hours = Number(clock[1]) % 24;
      minutes = Number(clock[2]);
      const half = (clock[3] || '').toLowerCase();
      if ((half === 'pm' || half === 'م') && hours < 12) hours += 12;
      if ((half === 'am' || half === 'ص') && hours === 12) hours = 0;
    }
    return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]), hours, minutes).getTime();
  }
  return Date.parse(text);
}

function vitalsLine(payload = {}) {
  return VITALS.filter(([key]) => payload[key] !== null && payload[key] !== undefined && payload[key] !== '')
    .map(([key, label, unit]) => `${label} ${payload[key]}${unit}`)
    .join(' · ');
}

function resultLine(request) {
  const r = request.result || {};
  if (Array.isArray(r.values) && r.values.length) {
    return r.values
      .slice(0, 4)
      .map((v) => `${v.test}: ${v.value}${v.unit ? ` ${v.unit}` : ''}`)
      .join(' · ');
  }
  if (Array.isArray(r.dispensed) && r.dispensed.length) return r.dispensed.map((d) => d.name).join('، ');
  return r.impression || r.summary || r.report || '';
}

/**
 * @param {object} file   GET /api/patients/:id/file
 * @param {object} [opts]
 * @param {number} [opts.limit=300]
 * @returns {Array<{ key, kind, at, title, detail, chips: string[] }>}
 */
export function buildTimeline(file, { limit = 300 } = {}) {
  if (!file || !file.patient) return [];
  const rows = [];
  const push = (kind, id, at, title, detail = '', chips = []) => {
    if (!Number.isFinite(at)) return;
    rows.push({ key: `${kind}-${id}`, kind, at, title, detail: clip(detail), chips: chips.filter(Boolean) });
  };

  const p = file.patient;
  push(
    'registration',
    p.id,
    momentOf(p.createdAt),
    'فُتح الملف الطبي',
    [file.contractParty?.name && `جهة التعاقد: ${file.contractParty.name}`, p.complaints && `الشكوى: ${p.complaints}`]
      .filter(Boolean)
      .join(' — '),
    [p.patientType === 'insured' ? 'تأمين' : 'نقدي'],
  );

  for (const v of file.visits || []) {
    const complaint = v.intake?.chiefComplaint;
    const answers = (v.intake?.customFields || []).map((f) => `${f.label}: ${f.value === true ? 'نعم' : f.value === false ? 'لا' : f.value}`);
    push(
      'visit',
      v.id,
      momentOf(v.date, v.time),
      ['زيارة', v.doctorName, v.clinicName].filter(Boolean).join(' · '),
      [complaint && `الشكوى: ${complaint}`, ...answers].filter(Boolean).join(' — '),
      [v.status],
    );
  }

  for (const r of file.receipts || []) {
    const lines = (r.items || []).map((line) => line.label).filter(Boolean);
    const money = r.total !== undefined ? `الإجمالي ${formatMoney(r.total)} · المدفوع ${formatMoney(r.paidAmount ?? 0)}` : '';
    push(
      'receipt',
      r.id,
      momentOf(r.issuedAt || r.createdAt),
      `${r.receiptType === 'deferred' ? 'إيصال آجل' : 'إيصال نقدي'} ${r.number || ''}`.trim(),
      [lines.join('، '), money].filter(Boolean).join(' — '),
      [RECEIPT_STATUS[r.status] || r.status, r.signatureName && `توقيع ${r.signatureName}`],
    );
  }

  for (const r of file.requests || []) {
    const dept = DEPARTMENT_META[r.department]?.label || r.department;
    push(
      'request',
      r.id,
      momentOf(r.createdAt),
      `${dept} — ${r.title}`,
      [r.requestedByName && `من ${r.requestedByName}`, r.clinicName, r.notes].filter(Boolean).join(' · '),
      [
        STATUS_META[r.status]?.label || r.status,
        r.receipt?.number && `بإيصال ${r.receipt.number}`,
        r.billingStatus && r.billingStatus !== 'none' && BILLING_META[r.billingStatus]?.label,
      ],
    );
    if (r.status === 'completed') {
      push('result', r.id, momentOf(r.completedAt || r.updatedAt), `نتيجة ${dept} — ${r.title}`, resultLine(r), [r.handledByName]);
    }
  }

  for (const n of file.notes || []) {
    push('note', n.id, momentOf(n.createdAt), NOTE_LABELS[n.kind] || n.kind, n.text, [n.createdByName]);
  }

  for (const rx of file.prescriptions || []) {
    const medicines = (rx.medicines || []).map((m) => m.name).filter(Boolean);
    push(
      'prescription',
      rx.id,
      momentOf(rx.createdAt || rx.date),
      ['روشتة', rx.doctorName].filter(Boolean).join(' · '),
      [rx.diagnosis && `التشخيص: ${rx.diagnosis}`, medicines.join('، ')].filter(Boolean).join(' — '),
    );
  }

  for (const n of file.nursing || []) {
    const detail =
      n.kind === 'vitals'
        ? vitalsLine(n.payload)
        : n.kind === 'medication'
          ? [n.payload?.name, n.payload?.dose, n.payload?.route].filter(Boolean).join(' ')
          : n.text;
    push('nursing', n.id, momentOf(n.recordedAt), n.kind === 'vitals' ? 'علامات حيوية' : n.kind === 'medication' ? 'إعطاء دواء' : 'ملاحظة تمريض', detail || n.text, [n.recordedByName]);
  }

  for (const b of file.beds || []) {
    push('bed', b.id, momentOf(b.startAt), `سرير #${b.bedId}`, '', [ASSIGNMENT_META[b.status]?.label || b.status]);
  }

  for (const a of file.attachments || []) {
    push('attachment', a.id, momentOf(a.createdAt), a.title || a.fileName || 'مرفق', a.category || '', [a.uploadedByName]);
  }

  return rows.sort((a, b) => b.at - a.at).slice(0, limit);
}

/** The rows a filter chip keeps. */
export function filterTimeline(rows, filterId) {
  const filter = TIMELINE_FILTERS.find((f) => f.id === filterId);
  if (!filter || !filter.group) return rows;
  return rows.filter((row) => TIMELINE_KINDS[row.kind]?.group === filter.group);
}
